// 🔹 Stylesheets
import "./FaceClockInOut.css";

// 🔹 React Core
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// 🔹 Routing
import { useLocation, useNavigate } from "react-router-dom";

// 🔹 State Management
import { useDispatch } from "react-redux";

// 🔹 Capacitor & Mobile
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Network } from "@capacitor/network";
import { EdgeToEdge } from "@capawesome/capacitor-android-edge-to-edge-support";
import { StatusBar, Style } from "@capacitor/status-bar";

// 🔹 UI Component Libraries
import { Dropdown, Menu, Modal, Button, Spin, message } from "antd";
import { LoadingOutlined, CloseCircleFilled, InfoCircleOutlined } from "@ant-design/icons";

// 🔹 Internal Components
import PermissionDeniedModal from "./PermissionDeniedModal";
import SelectOffSiteLocationModal from "./SelectOffSiteLocationModal";
import { hr } from "../Assets/hr";

// 🔹 Internal Utilities
import { DecriptEnv, getFullAwsUrl, ROLE, IS_WEB_ATTENDANCE_ENABLED } from "../helpers/utils";
import { dispatchToast } from "../helpers/dispatch";
import { getDecryptedName, getFcmDecryptedToken, getDecryptedUserData, decryptByText } from "../helpers/bycrypt";
import {
  assertClearFaceCapture,
  fixImageRotation,
  isLookingStraight,
  processFaceImageFrontend,
  compareFaceSignatures,
  enhanceLowLightImage,
} from "../helpers/faceDetectionUtils";
import { requestCameraPermission } from "../helpers/nativePermissions";
import { useEmployeeSync } from "../helpers/useEmployeeSync";
import { useAttendanceSync } from "../helpers/useAttendanceSync";
import { useSocket } from "../contexts/SocketContext";

import { TrueTimeService } from "../helpers/timeUtils";
import { userStorage } from "../helpers/userStorage";
import { enqueueRecord, getPendingRecords } from "../helpers/attendanceQueueStorage";
import { attendanceQueueManager } from "../helpers/attendanceQueueManager";
import { serverHealthService } from "../helpers/serverHealthService";

// 🔹 Plugins
import {
  ClassificationMode,
  ContourMode,
  FaceDetection,
  FACE_DISTANCE_FACTOR,
  LandmarkMode,
  LensFacing,
  PerformanceMode,
  selectPrimaryFace,
  type Face,
} from "../plugins/faceDetection";
import { Proximity } from "../plugins/proximity";
import { OrientationLock } from "../plugins/orientationLock";
import { SystemUi } from "../plugins/systemUi";
import { AppLifecycle, isAppLifecycleAvailable } from "../plugins/appLifecycle";
import { trackDeviceCloseAndWait } from "../helpers/deviceTracking";

// 🔹 Redux Actions
import { LogoutAPI } from "../Auth/Redux/auth";
import {
  AddEmployeeAttendanceAPI,
  MatchFaceSignatureAPI,
} from "../Admin/Redux/employeeAttendance";

// 🔹 Page Constants
import {
  CLIENT_FILTER_KEY,
  CLIENT_REFETCH_KEY,
  CLIENT_SCROLL_KEY,
} from "../Admin/Pages/ClientsDistributors/clientDistributorTypes";
import {
  INSTALLATION_PLAN_FILTER_KEY,
  INSTALLATION_PLAN_REFETCH_KEY,
  INSTALLATION_PLAN_SCROLL_KEY,
} from "../Admin/Pages/InstallationPlan/InstallationPlan";
import {
  INVOICE_FILTER_KEY,
  INVOICE_REFETCH_KEY,
  INVOICE_SCROLL_KEY,
} from "../Admin/Pages/Invoice/invoiceTypes";
import {
  ORDER_FILTER_KEY,
  ORDER_REFETCH_KEY,
  ORDER_SCROLL_KEY,
} from "../Admin/Pages/Orders/orderTypes";
import {
  PRODUCT_FILTER_KEY,
  PRODUCT_REFETCH_KEY,
  PRODUCT_SCROLL_KEY,
} from "../Admin/Pages/Products/productsTypes";
import {
  QUOTE_REQUEST_FILTER_KEY,
  QUOTE_REQUEST_REFETCH_KEY,
  QUOTE_REQUEST_SCROLL_KEY,
} from "../Admin/Pages/QuoteRequests/QuoteRequests";
import {
  QUOTATION_FILTER_KEY,
  QUOTATION_REFETCH_KEY,
  QUOTATION_SCROLL_KEY,
} from "../Admin/Pages/Quotations/quotationTypes";
import {
  SALES_COMMISSION_FILTER_KEY,
  SALES_COMMISSION_REFETCH_KEY,
  SALES_COMMISSION_SCROLL_KEY,
} from "../Admin/Pages/SalesCommission/salesCommissionTypes";
import { RootState, useAppSelector } from "../store";
import { BtnLoader } from "./Components";



const IDLE_TIMEOUT_MS = 1 * 60 * 1000; // 1 minute — time before screen dims when no face detected
const FACE_CAPTURE_DELAY_MS = 1000; // 1 seconds — wait for person to align face properly before capture
const SCAN_NO_FACE_TIMEOUT_MS = 30 * 1000; // 30 seconds — timeout before scan button re-enables if no face is found

// NOTE: the face distance factor is configured in ONE place —
// FACE_DISTANCE_FACTOR in ../plugins/faceDetection.ts (shared by Web,
// Android & iOS). It is passed to startActiveScan() below.

// --- Adaptive face-lost detection ---
const FACE_LOST_GRACE_FLOOR_MS = 500;
const FACE_LOST_GRACE_CEILING_MS = 2000;
const FACE_LOST_GRACE_MULTIPLIER = 3;
const FRAME_GAP_SAMPLE_SIZE = 8;

const COUNTDOWN_HIDE_DELAY_MS = 500;

// Safety-net for processing lock deadlock
const PROCESSING_LOCK_SAFETY_NET_MS = 8000;
const FACE_SCAN_DELAY_MS = 20000;

function waitForNextPaint(maxWaitMs: number = 600): Promise<void> {
  const painted = new Promise<void>((resolve) => {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 50);
        });
      });
    } else {
      setTimeout(resolve, 50);
    }
  });
  // Safety valve — never stall the scan flow if rAF gets throttled/hidden.
  const bailOut = new Promise<void>((resolve) => setTimeout(resolve, maxWaitMs));
  return Promise.race([painted, bailOut]);
}

// Tracks if the initial first-time face-api.js local scan & model initialization
// has been completed. Only the first local scan on Attendance Tablet takes time
// because models are loaded from disk into memory; subsequent local scans and
// personal face scans (which call the server API) do not need the loader.
// Once models are loaded on mount, this flips to true so subsequent component
// mounts skip the upfront loader entirely.
let hasCompletedFirstLocalScan = false;

export const FaceClockInOut = ({ isPersonalFaceScan, onSuccessClose }: { isPersonalFaceScan?: boolean; onSuccessClose?: () => void }) => {
  const userData = getDecryptedUserData();
  const roleKey = userData?.role?.key || "";
  const roleName = String(userData?.role?.name || "").toLowerCase();
  const isAttendanceTabletRole =
    roleKey === ROLE.ATTENDANCE_TABLET ||
    roleName.includes("attendance tablet") ||
    roleName.includes("attendance-tablet");
  const requiresScanButton = !isPersonalFaceScan || isAttendanceTabletRole;
  const { authLoading } = useAppSelector((state: RootState) => state.loading)
  // Web attendance flag: when REACT_APP_ENABLE_WEB_ATTENDANCE=true, the
  // Attendance Tablet on web matches faces through the server API
  // (`match-face`, same as the personal face scan) instead of the local
  // SQLite DB (native-only), and clock in/out goes through the same API
  // queue. Native apps always keep the local offline-first SQLite flow.
  const useServerFaceMatch =
    isPersonalFaceScan ||
    (!Capacitor.isNativePlatform() && IS_WEB_ATTENDANCE_ENABLED);
  // The full-screen "Getting Things Ready" loader and the face-api.js model
  // warm-up are only needed for local (SQLite) matching on the native
  // Attendance Tablet — the server match never uses local models.
  const usesLocalFaceMatching = !useServerFaceMatch;

  const [isScanActive, setIsScanActive] = useState(!requiresScanButton);
  const isScanActiveRef = useRef(isScanActive);
  useEffect(() => {
    isScanActiveRef.current = isScanActive;
  }, [isScanActive]);

  const scanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Auto-reset timers shown after a success card (3s) / error card (2s). They are
  // kept in refs so a quick "Click to scan" / "Try Again" restart can cancel the
  // pending callback — otherwise it would fire later and stop the fresh scan.
  const successResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [scanStatus, setScanStatus] = useState(
    requiresScanButton ? "Press 'CLICK TO SCAN' to start" : "Initializing..."
  );
  const [, setFaceDetected] = useState(false);
  const [showPermissionDeniedModal, setShowPermissionDeniedModal] =
    useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  useEffect(() => {
    if (showLogoutModal) {
      document.documentElement.classList.add("logout-confirmation-modal");
    } else {
      document.documentElement.classList.remove("logout-confirmation-modal");
    }
    return () => {
      document.documentElement.classList.remove("logout-confirmation-modal");
    };
  }, [showLogoutModal]);

  const [captureCountdown, setCaptureCountdown] = useState(0);

  // Full-screen blocking loader shown during the first-time face-api.js
  // model pre-load on Attendance Tablet (shown on mount, before any face is detected).
  // Start visible immediately if models haven't been loaded yet so users see
  // "Getting Things Ready" as soon as the scanner opens, not on the first face.
  const [showProcessingLoader, setShowProcessingLoader] = useState(
    usesLocalFaceMatching && !hasCompletedFirstLocalScan
  );
  const showProcessingLoaderRef = useRef(showProcessingLoader);
  useEffect(() => {
    showProcessingLoaderRef.current = showProcessingLoader;
  }, [showProcessingLoader]);

  // PPE Modal States
  const [showPPEModal, setShowPPEModal] = useState(false);
  const showPPEModalRef = useRef(showPPEModal);
  useEffect(() => {
    showPPEModalRef.current = showPPEModal;
  }, [showPPEModal]);
  const [ppePayload, setPpePayload] = useState<any>(null);
  const [ppeMatchUser, setPpeMatchUser] = useState<any>(null);
  const ppeClockInFnRef = useRef<((payloadOverride?: any) => Promise<void>) | null>(null);

  // Off Site Location Modal States
  const [showOffSiteLocationModal, setShowOffSiteLocationModal] = useState(false);
  const [isOffSiteSubmitting, setIsOffSiteSubmitting] = useState(false);
  const pendingOffSiteClockInFnRef = useRef<((locationValues?: any) => Promise<void>) | null>(null);

  const [matchedEmployeeData, setMatchedEmployeeData] = useState<{
    name: string, department: string, photo: string;
    fallbackPhoto?: string;
    isClockedIn: boolean;
  } | null>(null);
  const [scanErrorState, setScanErrorState] = useState<string | null>(null);
  const [isWebPermissionDenied, setIsWebPermissionDenied] = useState(false);
  const [isLandscape, setIsLandscape] = useState(
    window.innerWidth > window.innerHeight
  );

  const { triggerAttendanceSync } = useAttendanceSync();
  const { isSyncing, syncProgress, triggerSync } = useEmployeeSync();

  const dispatch = useDispatch();
  const [isManualSync, setIsManualSync] = useState(false);
  const { socket } = useSocket();

  // Face accuracy threshold — loaded from persisted business settings on mount
  // and updated in real-time via the hr:settings-updated socket event.
  const faceAccuracyThresholdRef = useRef<number>(50);

  useEffect(() => {
    document.body.classList.add('face-scanner-app-opened');
    return () => {
      document.body.classList.remove('face-scanner-app-opened');
    };
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleFaceRegistered = () => {
      console.log("FaceClockInOut: employee-auto-sync received, triggering sync...");
      triggerSync();
    };

    const handlePPEUpdate = async (data: { updates: { userId: string; isPPEKitRequired: boolean }[] }) => {
      console.log("FaceClockInOut: ppe:settings-updated received, updating userStorage...");
      if (data && data.updates && data.updates.length > 0) {
        const { userStorage } = await import("../helpers/userStorage");
        await userStorage.updatePPEStatus(data.updates);
      }
    };

    const handleHRSettingsUpdate = (data: { faceAccuracyThreshold?: number, attendance_app_schedule?: any }) => {
      console.log("FaceClockInOut: hr:settings-updated received:", data);
      if (typeof data?.faceAccuracyThreshold === "number") {
        faceAccuracyThresholdRef.current = data.faceAccuracyThreshold;
        console.log(`FaceClockInOut: faceAccuracyThreshold updated to ${data.faceAccuracyThreshold}%`);
      }
      // Re-arm the auto close / auto start schedule immediately when the HR settings
      // carry a new attendance_app_schedule (see helpers/appLifecycleScheduler).
      if (data?.attendance_app_schedule) {
        window.dispatchEvent(
          new CustomEvent("business-settings-updated", { detail: data }),
        );
      }
      // Any HR settings update should trigger a background sync to fetch new timezone, PPE settings, etc.
      triggerSync();
    };

    socket.on("employee-auto-sync", handleFaceRegistered);
    socket.on("ppe:settings-updated", handlePPEUpdate);
    socket.on("hr:settings-updated", handleHRSettingsUpdate);
    socket.on("employee-attendance-updated", handleFaceRegistered);

    return () => {
      socket.off("employee-auto-sync", handleFaceRegistered);
      socket.off("ppe:settings-updated", handlePPEUpdate);
      socket.off("hr:settings-updated", handleHRSettingsUpdate);
      socket.off("employee-attendance-updated", handleFaceRegistered);
    };
  }, [socket, triggerSync]);

  const previousIsSyncing = useRef(isSyncing);
  useEffect(() => {
    if (previousIsSyncing.current && !isSyncing && syncProgress === 100) {
      if (isManualSync) {
        message.success({
          content: "Sync completed successfully!",
          className: "custom-sync-toaster",
        });
        setIsManualSync(false);
      }
    }
    previousIsSyncing.current = isSyncing;
  }, [isSyncing, syncProgress, dispatch, isManualSync]);

  const logoutHandler = () => {
    const savedToken = getFcmDecryptedToken();
    dispatch(LogoutAPI({ fcm: savedToken }, navigate) as any);
    if (location?.pathname?.includes("products")) {
      sessionStorage.removeItem(PRODUCT_FILTER_KEY);
      sessionStorage.removeItem(PRODUCT_SCROLL_KEY);
      sessionStorage.setItem(PRODUCT_REFETCH_KEY, "1");
    }
    if (location?.pathname?.includes("orders")) {
      sessionStorage.removeItem(ORDER_FILTER_KEY);
      sessionStorage.removeItem(ORDER_SCROLL_KEY);
      sessionStorage.setItem(ORDER_REFETCH_KEY, "1");
    }
    if (location?.pathname?.includes("quotations")) {
      sessionStorage.removeItem(QUOTATION_FILTER_KEY);
      sessionStorage.removeItem(QUOTATION_SCROLL_KEY);
      sessionStorage.setItem(QUOTATION_REFETCH_KEY, "1");
    }
    if (location?.pathname?.includes("clients-distributors")) {
      sessionStorage.removeItem(CLIENT_FILTER_KEY);
      sessionStorage.removeItem(CLIENT_SCROLL_KEY);
      sessionStorage.setItem(CLIENT_REFETCH_KEY, "1");
    }
    if (location?.pathname?.includes("installation-plan")) {
      sessionStorage.removeItem(INSTALLATION_PLAN_FILTER_KEY);
      sessionStorage.removeItem(INSTALLATION_PLAN_SCROLL_KEY);
      sessionStorage.setItem(INSTALLATION_PLAN_REFETCH_KEY, "1");
    }
    if (location?.pathname?.includes("quote-requests")) {
      sessionStorage.removeItem(QUOTE_REQUEST_FILTER_KEY);
      sessionStorage.removeItem(QUOTE_REQUEST_SCROLL_KEY);
      sessionStorage.setItem(QUOTE_REQUEST_REFETCH_KEY, "1");
    }
    if (location?.pathname?.includes("invoices")) {
      sessionStorage.removeItem(INVOICE_FILTER_KEY);
      sessionStorage.removeItem(INVOICE_SCROLL_KEY);
      sessionStorage.setItem(INVOICE_REFETCH_KEY, "1");
    }
    if (location?.pathname?.includes("sales-commission")) {
      sessionStorage.removeItem(SALES_COMMISSION_FILTER_KEY);
      sessionStorage.removeItem(SALES_COMMISSION_SCROLL_KEY);
      sessionStorage.setItem(SALES_COMMISSION_REFETCH_KEY, "1");
    }

    // Clear Manufacturing (Job) session keys
    sessionStorage.removeItem("mfg_filter_open");
    sessionStorage.removeItem("mfg_machine_id");
    sessionStorage.removeItem("mfg_department_id");
    sessionStorage.removeItem("mfg_employee_id");
    sessionStorage.removeItem("mfg_active_tab");
    sessionStorage.removeItem("mfg_selected_date");
  };

  const navigate = useNavigate();
  const location = useLocation();

  const currentSignatureRef = useRef<number[] | null>(null);
  const processingRef = useRef(false);
  const isScanningRef = useRef(false);
  // Guards against stale async operations (stopScanner racing startScanner).
  // Incremented on every start/stop — any in-flight async check that sees a
  // different session number knows it's stale and must abort its work.
  const scanSessionRef = useRef(0);
  const lastScanTimeRef = useRef<{ [userId: string]: number }>({});
  const straightFaceStartTimeRef = useRef<number | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const facePresentRef = useRef(false);

  const faceLostTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const faceHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Adaptive frame-gap measurement ---
  const lastFrameAtRef = useRef<number | null>(null);
  const frameGapSamplesRef = useRef<number[]>([]);
  const currentGraceMsRef = useRef<number>(FACE_LOST_GRACE_FLOOR_MS);

  // --- Safety-net for the processing lock ---
  const processingSafetyNetRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const isNative = Capacitor.isNativePlatform();

  // Warn when the scanner is opened on web while the web attendance flag is
  // disabled (login is already blocked in that case, so this is mainly
  // defence-in-depth for direct URL access). With
  // REACT_APP_ENABLE_WEB_ATTENDANCE=true the scanner works on web and no
  // warning is shown.
  useEffect(() => {
    if (!isNative && !IS_WEB_ATTENDANCE_ENABLED) {
      message.error("Clock in/out is not supported on web.");
    }
  }, [isNative]);

  // Lock the app to portrait orientation while the face scan screen is active.
  // This keeps the camera and UI from rotating when the device is turned.
  // The lock is released when the component unmounts (user navigates away),
  // allowing other screens to rotate normally.
  useEffect(() => {
    if (!isNative) return;
    OrientationLock.lockPortrait().catch((e: any) =>
      console.error("Failed to lock portrait orientation:", e),
    );
    return () => {
      OrientationLock.unlockOrientation().catch((e: any) =>
        console.error("Failed to unlock orientation:", e),
      );
    };
  }, [isNative]);

  // ── Attendance Tablet (Android): hide the system bars ──────────────────────
  // While the tablet's scanning screen is open we run in kiosk-style fullscreen
  // so the device is fully focused on our app:
  //   • bottom Android navigation bar (back / home / recent-apps buttons and
  //     any launcher/app icons it hosts),
  //   • top Android status bar (clock / wifi / battery).
  // The bars are restored automatically when this screen unmounts. This is
  // Android-only and ONLY for the Attendance Tablet role — personal face scans
  // on phones keep the system bars untouched.
  useEffect(() => {
    const shouldHideBars =
      isNative && Capacitor.getPlatform() === "android" && isAttendanceTabletRole;

    if (!shouldHideBars) return;

    SystemUi.hideBars().catch((e: any) =>
      console.error("Failed to hide Android system bars:", e),
    );

    return () => {
      SystemUi.showBars().catch((e: any) =>
        console.error("Failed to restore Android system bars:", e),
      );
    };
  }, [isNative, isAttendanceTabletRole]);

  // Load stored face count on mount
  useEffect(() => {
    // console.log("FaceClockInClockOut: Initializing component mount");

    setScanStatus("Initializing...");

    // Load the persisted face accuracy threshold from local storage.
    // This is set during the employee sync and updated via socket events.
    import("../helpers/userStorage").then(({ userStorage }) => {
      userStorage.getBusinessSettings().then((settings) => {
        if (typeof settings?.faceAccuracyThreshold === "number") {
          faceAccuracyThresholdRef.current = settings.faceAccuracyThreshold;
          console.log(`FaceClockInOut: Loaded faceAccuracyThreshold from storage: ${settings.faceAccuracyThreshold}%`);
        }
      }).catch(() => { });
    });

    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    window.addEventListener("resize", handleResize);

    const appStateListener = CapacitorApp.addListener('appStateChange', async (state) => {
      if (!state.isActive) {
        await stopScanner();
      } else {
        if (!isScanningRef.current && !matchedEmployeeData && !showProcessingLoaderRef.current) {
          startScanner();
        }
      }
    });

    // ── Eagerly pre-load face-api.js models then run the full analyzeFace
    // pipeline warm-up on a dummy image — so the first real face scan is instant.
    // On subsequent mounts (hasCompletedFirstLocalScan === true) this is a no-op.
    if (usesLocalFaceMatching && !hasCompletedFirstLocalScan) {
      import("../helpers/faceDetectionUtils").then(({ loadFaceApiModels, warmUpFaceApiModels }) => {
        loadFaceApiModels()
          .then(() => warmUpFaceApiModels())
          .then(() => {
            hasCompletedFirstLocalScan = true;
            setShowProcessingLoader(false);
          })
          .catch((err: any) => {
            console.error("FaceClockInClockOut: Eager model pre-load/warm-up failed:", err);
            // Don't block the user — hide the loader and let analyzeFace surface
            // the error naturally if it occurs again during an actual scan.
            setShowProcessingLoader(false);
          });
      });
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      appStateListener.then(l => l.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopCountdownDisplay = () => {
    // console.log("FaceClockInClockOut: stopCountdownDisplay");
    straightFaceStartTimeRef.current = null;
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const recordFrameGap = (now: number) => {
    // console.log("FaceClockInClockOut: recordFrameGap");
    if (lastFrameAtRef.current !== null) {
      const gap = now - lastFrameAtRef.current;
      if (gap > 0 && gap < FACE_LOST_GRACE_CEILING_MS * 2) {
        const samples = frameGapSamplesRef.current;
        samples.push(gap);
        if (samples.length > FRAME_GAP_SAMPLE_SIZE) {
          samples.shift();
        }
        const avg = samples.reduce((sum, g) => sum + g, 0) / samples.length;
        const adaptiveGrace = Math.min(
          FACE_LOST_GRACE_CEILING_MS,
          Math.max(
            FACE_LOST_GRACE_FLOOR_MS,
            Math.round(avg * FACE_LOST_GRACE_MULTIPLIER),
          ),
        );
        currentGraceMsRef.current = adaptiveGrace;
      }
    }
    lastFrameAtRef.current = now;
  };

  const handleFaceLost = useCallback(() => {
    // console.log("FaceClockInClockOut: handleFaceLost");
    if (!facePresentRef.current) return;

    facePresentRef.current = false;
    setFaceDetected(false);
    stopCountdownDisplay();

    if (faceHideTimeoutRef.current) {
      clearTimeout(faceHideTimeoutRef.current);
    }
    faceHideTimeoutRef.current = setTimeout(() => {
      if (!facePresentRef.current) {
        setCaptureCountdown(0);
      }
      faceHideTimeoutRef.current = null;
    }, COUNTDOWN_HIDE_DELAY_MS);
  }, []);

  const scheduleFaceLostCheck = useCallback(() => {
    // console.log("FaceClockInClockOut: scheduleFaceLostCheck");
    if (faceLostTimeoutRef.current) {
      clearTimeout(faceLostTimeoutRef.current);
    }
    faceLostTimeoutRef.current = setTimeout(() => {
      faceLostTimeoutRef.current = null;
      handleFaceLost();
    }, currentGraceMsRef.current);
  }, [handleFaceLost]);

  const clearFaceLostCheck = () => {
    // console.log("FaceClockInClockOut: clearFaceLostCheck");
    if (faceLostTimeoutRef.current) {
      clearTimeout(faceLostTimeoutRef.current);
      faceLostTimeoutRef.current = null;
    }
  };

  const lockProcessing = () => {
    // console.log("FaceClockInClockOut: lockProcessing");
    processingRef.current = true;
    if (processingSafetyNetRef.current) {
      clearTimeout(processingSafetyNetRef.current);
    }
    processingSafetyNetRef.current = setTimeout(() => {
      processingRef.current = false;
      processingSafetyNetRef.current = null;
    }, PROCESSING_LOCK_SAFETY_NET_MS);
  };

  const unlockProcessing = () => {
    // console.log("FaceClockInClockOut: unlockProcessing");
    processingRef.current = false;
    if (processingSafetyNetRef.current) {
      clearTimeout(processingSafetyNetRef.current);
      processingSafetyNetRef.current = null;
    }
  };

  const bodyBgRef = useRef<string>("");
  const htmlBgRef = useRef<string>("");
  const rootDisplayRef = useRef<string>("");
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isProximityMonitoringRef = useRef(false);
  const isIdleModeRef = useRef(false);

  const clearScanTimeout = useCallback(() => {
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }
  }, []);

  const clearSuccessResetTimer = useCallback(() => {
    if (successResetTimerRef.current) {
      clearTimeout(successResetTimerRef.current);
      successResetTimerRef.current = null;
    }
  }, []);

  const clearErrorResetTimer = useCallback(() => {
    if (errorResetTimerRef.current) {
      clearTimeout(errorResetTimerRef.current);
      errorResetTimerRef.current = null;
    }
  }, []);

  const handleStopScan = useCallback((statusMsg: string = "Press 'CLICK TO SCAN' to start") => {
    // console.log("FaceClockInOut: handleStopScan ->", statusMsg);
    clearScanTimeout();
    setIsScanActive(false);
    isScanActiveRef.current = false;
    stopCountdownDisplay();
    setCaptureCountdown(0);
    facePresentRef.current = false;
    clearFaceLostCheck();
    setScanStatus(statusMsg);
  }, [clearScanTimeout]);

  const resetIdleTimer = useCallback(() => {
    // console.log("FaceClockInClockOut: resetIdleTimer");
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = setTimeout(async () => {
      isIdleModeRef.current = true;

      // Enable face detection idle mode (low FPS)
      try {
        await FaceDetection.setIdleMode({ enabled: true });
      } catch (err) {
        console.error("Failed to enable FaceDetection idle mode:", err);
      }

      // Enable camera-based motion detection to detect hand waves, people walking by
      try {
        await FaceDetection.setMotionDetection({ enabled: true });
      } catch (err) {
        console.error("Failed to enable motion detection:", err);
      }

      if (Capacitor.isNativePlatform()) {
        try {
          await Proximity.dimScreen();
          // Start proximity + accelerometer monitoring for wake-up
          await Proximity.startMonitoring();
          isProximityMonitoringRef.current = true;
          setScanStatus(
            "Screen dimmed. Move, touch, or approach camera to wake...",
          );
        } catch (err) {
          console.error("Failed to enter idle mode:", err);
        }
      }
    }, IDLE_TIMEOUT_MS);
  }, [isNative]);

  const exitIdleMode = useCallback(async () => {
    // console.log("FaceClockInClockOut: exitIdleMode");
    // Ignore wake events when not actually in idle/dim mode. The native
    // proximity sensor can fire even when monitoring was already stopped
    // (race at the dim boundary), which would otherwise force brightness to 1.0.
    if (!isIdleModeRef.current) {
      // console.log("FaceClockInClockOut: exitIdleMode ignored, not in idle mode");
      return;
    }
    // Capture current session — if a stop/start happens while awaiting,
    // this wake is stale and must not affect the new scan session.
    const session = scanSessionRef.current;
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }

    isIdleModeRef.current = false;

    // Disable face detection idle mode
    try {
      await FaceDetection.setIdleMode({ enabled: false });
    } catch {
      // Ignore
    }
    if (session !== scanSessionRef.current) return;

    // Disable camera-based motion detection
    try {
      await FaceDetection.setMotionDetection({ enabled: false });
    } catch {
      // Ignore
    }
    if (session !== scanSessionRef.current) return;

    if (isProximityMonitoringRef.current) {
      try {
        await Proximity.stopMonitoring();
        isProximityMonitoringRef.current = false;
      } catch (err) {
        console.error("Failed to stop proximity monitoring:", err);
      }
    }
    if (session !== scanSessionRef.current) return;

    if (Capacitor.isNativePlatform()) {
      try {
        await Proximity.wakeScreen();
      } catch (err) {
        console.error("Failed to wake screen / restore brightness:", err);
      }
    }
    if (session !== scanSessionRef.current) return;

    // Reset face detection state
    stopCountdownDisplay();
    clearFaceLostCheck();
    setFaceDetected(false);
    facePresentRef.current = false;
    lastFrameAtRef.current = null;
    frameGapSamplesRef.current = [];

    if (requiresScanButton && !isScanActiveRef.current) {
      setScanStatus("Press 'CLICK TO SCAN' to start");
    } else {
      setScanStatus("Align face in the frame...");
    }

    resetIdleTimer();
  }, [resetIdleTimer, requiresScanButton]);

  const handleStartScan = useCallback(() => {
    console.log("FaceClockInOut: handleStartScan");
    // Cancel any pending auto-reset callbacks from a previous success/error card.
    // Without this, restarting the scan quickly (fast check in/out) would let the
    // stale timer fire later and immediately stop the scan we just started.
    clearSuccessResetTimer();
    clearErrorResetTimer();
    clearScanTimeout();
    // Release a processing lock left over from a previous capture/error/success
    // screen — otherwise every frame of this new session is ignored
    // (processingRef gate in the faceScanned handler) and the button appears dead.
    // The PPE modal owns the lock while it is open, so leave it untouched there.
    if (!showPPEModalRef.current) {
      unlockProcessing();
    }
    // Start a clean "align face" cycle: reset countdown, straight-face timer and
    // face-lost tracking so repeated clicks always restart the scan properly.
    stopCountdownDisplay();
    setCaptureCountdown(0);
    clearFaceLostCheck();
    facePresentRef.current = false;
    setIsScanActive(true);
    isScanActiveRef.current = true;
    setScanErrorState(null);
    setScanStatus("Align face in the frame...");
    resetIdleTimer();
    if (isIdleModeRef.current) {
      exitIdleMode();
    }

    scanTimeoutRef.current = setTimeout(() => {
      // console.log("FaceClockInOut: 30s scan timeout reached, re-enabling scan button");
      handleStopScan("Scan timed out. Press 'CLICK TO SCAN' to start");
    }, SCAN_NO_FACE_TIMEOUT_MS);
  }, [clearScanTimeout, clearSuccessResetTimer, clearErrorResetTimer, handleStopScan, resetIdleTimer, exitIdleMode]);

  const startScanner = useCallback(async () => {
    // console.log("FaceClockInOut: startScanner");
    // Drop any pending success/error auto-reset timers — a fresh scanner session
    // must never be torn down by a callback belonging to the previous one.
    clearSuccessResetTimer();
    clearErrorResetTimer();
    setIsWebPermissionDenied(false);
    // Mark web platform for full-screen camera UI (even before permission granted)
    if (!isNative) {
      document.documentElement.classList.add("web-platform");
      document.body.classList.add("web-platform");
      // Force black background + hide app root on web so previous page
      // content is never visible behind the scanner (even when the camera
      // permission prompt is shown or denied).
      document.body.style.setProperty("background", "#0b0e14", "important");
      document.documentElement.style.setProperty(
        "background",
        "#0b0e14",
        "important",
      );
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      const rootEl = document.getElementById("root");
      if (rootEl) {
        rootEl.style.display = "none";
      }
    }
    // Bump the session so any in-flight stale async operations (e.g. a previous
    // stopScanner that hasn't finished awaiting native plugin calls) know to
    // abort and not tear down this new session.
    const session = ++scanSessionRef.current;
    isScanningRef.current = true;
    unlockProcessing();
    currentSignatureRef.current = null;
    straightFaceStartTimeRef.current = null;
    facePresentRef.current = false;
    clearFaceLostCheck();
    lastFrameAtRef.current = null;
    frameGapSamplesRef.current = [];
    currentGraceMsRef.current = FACE_LOST_GRACE_FLOOR_MS;
    if (faceHideTimeoutRef.current) {
      clearTimeout(faceHideTimeoutRef.current);
      faceHideTimeoutRef.current = null;
    }
    setCaptureCountdown(0);

    // Ensure the screen is kept awake and brightness is boosted while scanning.
    // `originalBrightness` is captured once on the native side; subsequent calls
    // are no-ops for the capture but still set the brightness to full.
    if (Capacitor.isNativePlatform()) {
      try {
        await Proximity.keepAwake();
      } catch (e) {
        console.error("FaceClockInClockOut: keepAwake failed:", e);
      }
    }

    // Reset any lingering idle/proximity state from a previous session
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (isProximityMonitoringRef.current) {
      try {
        await Proximity.stopMonitoring();
      } catch (e) { }
      isProximityMonitoringRef.current = false;
    }
    if (isIdleModeRef.current) {
      try {
        await FaceDetection.setIdleMode({ enabled: false });
        await FaceDetection.setMotionDetection({ enabled: false });
      } catch (e) { }
      isIdleModeRef.current = false;
    }

    setScanErrorState(null);

    try {
      setScanStatus("Requesting camera permission...");
      const granted = await requestCameraPermission();
      if (!granted) {
        if (!isNative) {
          // On web, show a message in the scan screen instead of a popup
          // (we can't redirect to settings from a browser)
          setIsWebPermissionDenied(true);
          setScanStatus(
            "Camera permission is required. Please enable camera access in your browser settings, then refresh this page.",
          );
          setScanErrorState(
            "Camera permission is required. Please enable camera access in your browser settings, then refresh this page.",
          );
          return;
        }
        setScanStatus("Camera permission denied");
        setShowPermissionDeniedModal(true);
        return;
      }

      setScanStatus("Initializing face detector...");
      try {
        await FaceDetection.stopScan();
      } catch (e) { }
      await FaceDetection.removeAllListeners();

      await FaceDetection.addListener("faceScanned", async (event: any) => {
        // console.log("FaceClockInClockOut: faceScanned event received");
        // Ignore frames from a stale scan session (e.g. after app resume race)
        if (!isScanningRef.current || session !== scanSessionRef.current) return;
        const face: Face | null = event.face;
        const now = Date.now();

        // --- Multi-face policy (FACE_MULTI_FACE_POLICY in plugins/faceDetection.ts):
        // the most prominent (largest = closest) face is the scan candidate;
        // background / half-visible faces are IGNORED. The "Multiple faces
        // detected" error only triggers when another face is comparably
        // prominent (fully visible, similar size) — i.e. genuinely ambiguous. ---
        const eventFaces: Face[] =
          Array.isArray(event.faces) && event.faces.length > 0
            ? event.faces
            : face
              ? [face]
              : [];
        const { ambiguous: multipleProminentFaces } = selectPrimaryFace(
          eventFaces,
          event.frameWidth ?? 0,
          event.frameHeight ?? 0,
        );

        // If scan is not active (user hasn't pressed Scan button), do NOT process face recognition / countdown / match
        if (requiresScanButton && !isScanActiveRef.current) {
          if (face && isIdleModeRef.current) {
            await exitIdleMode();
          }
          return;
        }

        if (!face) {
          // Explicit "no face" signal from the plugin
          // In idle/dim mode, repeated "no face" frames arrive continuously
          // just to check for a wake-up. Skipping redundant state updates
          // while idle eliminates React churn in the background and keeps
          // the device cooler (these frames are re-emitted at only ~1fps,
          // but every one would otherwise trigger handleFaceLost + setState).
          if (isIdleModeRef.current) return;
          if (event.tooFar) {
            // A face WAS seen but rejected by the distance factor (someone
            // walking by farther away) — guide them closer, never scan.
            clearFaceLostCheck();
            handleFaceLost();
            setScanStatus("Move closer to the camera");
            return;
          }
          clearFaceLostCheck();
          handleFaceLost();
          return;
        }

        // --- We received a VALID face frame ---
        recordFrameGap(now);
        scheduleFaceLostCheck();

        facePresentRef.current = true;
        setFaceDetected(true);

        if (isIdleModeRef.current) {
          await exitIdleMode();
        } else {
          resetIdleTimer();
        }

        // If already processing a capture, ignore further frames
        if (processingRef.current) return;

        if (multipleProminentFaces) {
          setScanStatus("Multiple faces detected");
          stopCountdownDisplay();
          return;
        }

        if (!isLookingStraight(face)) {
          setScanStatus("Look straight at the camera");
          stopCountdownDisplay();
          return;
        }

        // --- Face IS present and looking straight ---
        if (straightFaceStartTimeRef.current === null) {
          straightFaceStartTimeRef.current = now;
          startCountdownDisplay(now);
        }

        const elapsed = now - straightFaceStartTimeRef.current;
        const remaining = Math.max(
          0,
          Math.ceil((FACE_CAPTURE_DELAY_MS - elapsed) / 1000),
        );
        setCaptureCountdown(remaining);

        if (elapsed >= FACE_CAPTURE_DELAY_MS) {
          lockProcessing();
          stopCountdownDisplay();
          setScanStatus("Capturing image...");
          // No loader shown here — models are already pre-loaded on mount.
          await analyzeFace(face);
        }
      });

      // Listen for camera-based motion detection (hand waves, people walking by)
      await FaceDetection.addListener("motionDetected", async () => {
        // console.log("FaceClockInClockOut: motionDetected event received");
        // Ignore motion events from a stale scan session
        if (session !== scanSessionRef.current) return;
        if (isIdleModeRef.current) {
          setScanStatus("Motion detected! Waking up...");
          await exitIdleMode();
        } else {
          // Even in active mode, motion resets the idle timer
          resetIdleTimer();
        }
      });

      await FaceDetection.addListener(
        "scanError",
        (err: { message: string }) => {
          setScanStatus(`Error: ${err.message}`);
        },
      );

      await FaceDetection.startActiveScan({
        lensFacing: LensFacing.Front,
        performanceMode: PerformanceMode.Fast,
        landmarkMode: LandmarkMode.All,
        contourMode: ContourMode.None,
        classificationMode: ClassificationMode.None,
        minFaceSize: 0.15,
        enableTracking: false,
        // Distance factor — tuned in ONE place: FACE_DISTANCE_FACTOR in
        // plugins/faceDetection.ts (applies to Web + Android + iOS).
        minFaceSizeRatio: FACE_DISTANCE_FACTOR.MIN_FACE_SIZE_RATIO,
        maxFaceSizeRatio: FACE_DISTANCE_FACTOR.MAX_FACE_SIZE_RATIO,
      });

      if (requiresScanButton && !isScanActiveRef.current) {
        setScanStatus("Press 'CLICK TO SCAN' to start");
      } else {
        setScanStatus("Align face in the frame...");
      }
      resetIdleTimer();
    } catch (error: any) {
      setScanStatus(`Failed: ${error.message || "Unknown error"}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNative, requiresScanButton, exitIdleMode, resetIdleTimer, clearSuccessResetTimer, clearErrorResetTimer]);

  const startCountdownDisplay = (startTime: number) => {
    // console.log("FaceClockInClockOut: startCountdownDisplay");
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    countdownIntervalRef.current = setInterval(() => {
      if (straightFaceStartTimeRef.current === null) {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        return;
      }

      const elapsed = Date.now() - straightFaceStartTimeRef.current;
      const remaining = Math.max(
        0,
        Math.ceil((FACE_CAPTURE_DELAY_MS - elapsed) / 1000),
      );
      setCaptureCountdown(remaining);
    }, 250);
  };


  const analyzeFace = async (face: Face) => {
    // console.log("FaceClockInClockOut: analyzeFace");
    clearScanTimeout();
    try {
      setScanStatus("Capturing image...");

      const frame = await FaceDetection.captureFrame();
      if (!frame || !frame.image) {
        setScanStatus("Failed to capture image. Try again.");
        setShowProcessingLoader(false);
        unlockProcessing();
        return;
      }

      const rotatedImageBase64 = await fixImageRotation(frame.image);
      // Normalize dark/night frames so the descriptor extracted (frontend local or
      // backend match) is robust — this is the key fix for poor night recognition.
      // Smart helper returns the original untouched on bright/day frames.
      const imageToUse = await enhanceLowLightImage(rotatedImageBase64);

      // ===== NATIVE (APP): HARD BLUR GATE =====
      // The web flow already validates the capture (face-api.js descriptor checks).
      // The app (native) had no clarity gate, so a motion-blurred frame (e.g. the
      // user moved at the last moment) could be matched/clocked. Reject clearly
      // blurry captures here with a clear error — the catch below shows it for a
      // couple of seconds, then the scan restarts so the user can simply re-align.
      if (isNative) {
        setScanStatus("Checking face clarity...");
        await assertClearFaceCapture(imageToUse);
      }

      let faceApiSignature: Float32Array | any = null;
      let userId: string = "", first_name: string = "", last_name: string = "", isClockedIn = false, bestMatch: any = null, decryptedName = "";

      if (useServerFaceMatch) {
        setScanStatus(
          isPersonalFaceScan
            ? "Verifying your face…"
            : "Identifying face via server...",
        );
        const imageToVerify = imageToUse;

        if (processingSafetyNetRef.current) {
          clearTimeout(processingSafetyNetRef.current);
          processingSafetyNetRef.current = null;
        }

        const matchResult: any = await new Promise((resolve, reject) => {
          (dispatch as any)(MatchFaceSignatureAPI(
            imageToVerify,
            (data: any) => resolve(data),
            (error: any) => reject(error)
          ));
        });

        const loggedInUser = getDecryptedUserData();
        // Self-match rule applies only to the personal face scan — the
        // Attendance Tablet on web may clock in/out ANY employee.
        if (
          isPersonalFaceScan &&
          String(matchResult.userId) !== String(loggedInUser?._id)
        ) {
          const matchedName = getDecryptedName({ first_name: matchResult.first_name, last_name: matchResult.last_name });
          const loggedInName = loggedInUser?.name || `${loggedInUser?.first_name || ""} ${loggedInUser?.last_name || ""}`.trim() || "Unknown";
          // throw new Error(`Face does not match the logged-in user. (Matched: ${matchedName} vs Logged in: ${loggedInName})`);
          throw new Error(`You can only clock in or out using your own face.`);
        }

        userId = matchResult.userId;
        first_name = matchResult.first_name;
        last_name = matchResult.last_name;
        isClockedIn = matchResult.isClockedIn;
        bestMatch = {
          ...matchResult,
          department_name: matchResult.department,
          isPPEKitRequired: matchResult.isPPEKitRequired ?? (loggedInUser?.isPPEKitRequired || false)
        };
        decryptedName = getDecryptedName({ first_name, last_name });

        // Mock a signature for the payload since we used API
        faceApiSignature = new Float32Array();

        if (!isPersonalFaceScan) {
          // Attendance Tablet on web — apply the same safeguards as the local
          // (native) flow: throttle rapid re-scans and reconcile the in/out
          // state with records still sitting in the offline queue.
          const nowMs = Date.now();
          const lastScan = lastScanTimeRef.current[String(userId)];
          if (lastScan && nowMs - lastScan < FACE_SCAN_DELAY_MS) {
            const remainingSec = Math.ceil((FACE_SCAN_DELAY_MS - (nowMs - lastScan)) / 1000);
            throw new Error(`Please wait ${remainingSec}s before scanning again.`);
          }

          const pendingRecords = await getPendingRecords();
          const userPending = pendingRecords.filter((r) => r.user === String(userId));
          if (userPending.length > 0) {
            const lastPending = userPending[userPending.length - 1];
            isClockedIn = lastPending.type === "clock-in";
          }

          const optStatus = attendanceQueueManager.getOptimisticStatus(String(userId));
          if (optStatus) {
            isClockedIn = optStatus === "clock-in";
          }
        }
      } else {
        let extractedData;
        try {
          extractedData = await processFaceImageFrontend(imageToUse);
          hasCompletedFirstLocalScan = true;
        } catch (err: any) {
          throw new Error(err.message || "Failed to extract face features using face-api.js.");
        }

        faceApiSignature = extractedData.descriptor;

        setScanStatus("Identifying face locally...");

        if (processingSafetyNetRef.current) {
          clearTimeout(processingSafetyNetRef.current);
          processingSafetyNetRef.current = null;
        }

        const users = await userStorage.getAllUsers();
        let maxConfidence = 0;

        for (const u of users) {
          if (!u.face_signature) continue;
          try {
            let sigRaw = u.face_signature;
            const sig = JSON.parse(sigRaw);
            if (sig && Array.isArray(sig) && sig.length === faceApiSignature.length) {
              const { confidence, distance } = compareFaceSignatures(faceApiSignature, sig);
              if (confidence > maxConfidence) {
                maxConfidence = confidence;
                bestMatch = u;
              }
            }
          } catch (e) {
          }
        }

        if (!bestMatch || maxConfidence < faceAccuracyThresholdRef.current) {
          throw new Error("No registered faces found");
        }

        userId = bestMatch._id;
        first_name = bestMatch.first_name;
        last_name = bestMatch.last_name;
        decryptedName = getDecryptedName({ first_name, last_name });

        const empDetails = typeof bestMatch.employee_details === 'string' ? JSON.parse(bestMatch.employee_details) : bestMatch.employee_details;
        const startDateStr = empDetails?.basic_details?.employment_start_date;
        if (startDateStr) {
          const startDate = new Date(startDateStr);
          const now = new Date();
          startDate.setHours(0, 0, 0, 0);
          now.setHours(0, 0, 0, 0);
          if (now.getTime() < startDate.getTime()) {
            throw new Error("Cannot clock in before joining date.");
          }
        }

        const nowMs = Date.now();
        const lastScan = lastScanTimeRef.current[String(userId)];
        if (lastScan && nowMs - lastScan < FACE_SCAN_DELAY_MS) {
          const remainingSec = Math.ceil((FACE_SCAN_DELAY_MS - (nowMs - lastScan)) / 1000);
          throw new Error(`Please wait ${remainingSec}s before scanning again.`);
        }

        let serverAttendances: any[] = [];
        try {
          let parsedArray = [];
          if (Array.isArray(bestMatch.employeeAttendance)) {
            parsedArray = bestMatch.employeeAttendance;
          } else if (typeof bestMatch.employeeAttendance === "string") {
            parsedArray = JSON.parse(bestMatch.employeeAttendance);
          }
          serverAttendances = parsedArray.map((record: any) => {
            if (typeof record === "string") {
              try {
                return JSON.parse(decryptByText(record));
              } catch (e) {
                return record;
              }
            }
            return record;
          });
        } catch (e) { }

        if (serverAttendances && serverAttendances.length > 0) {
          serverAttendances.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
          const lastServerRecord = serverAttendances[serverAttendances.length - 1];
          if (lastServerRecord && !lastServerRecord.clockOut) {
            isClockedIn = true;
          }
        }

        const pendingRecords = await getPendingRecords();
        const userPending = pendingRecords.filter(r => r.user === String(userId));
        if (userPending.length > 0) {
          const lastPending = userPending[userPending.length - 1];
          isClockedIn = lastPending.type === "clock-in";
        }

        const optStatus = attendanceQueueManager.getOptimisticStatus(String(userId));
        if (optStatus) {
          isClockedIn = optStatus === "clock-in";
        }
      }
      // Face matched / response received — remove the full-screen wait loader
      // and hand over to the normal welcome/clocking UI.
      setShowProcessingLoader(false);
      setScanStatus(`Welcome ${decryptedName}. Clocking ${isClockedIn ? 'out' : 'in'}...`);

      let photoKey = "";

      const attendanceType = isClockedIn ? "clock-out" : "clock-in";
      const attendancePayload = {
        user: userId,
        type: attendanceType,
        biometric_authentication_by: "face",
        biometric_authentication: {
          face: {
            photo: "",
            Signature: Array.from(faceApiSignature),
          },
        },
        photoBase64: rotatedImageBase64 || "",
      };

      const executeClockIn = async (payloadOverride: any = {}) => {
        const finalPayload = { ...attendancePayload, ...payloadOverride };
        attendanceQueueManager.setOptimisticStatus(String(userId), attendanceType as "clock-in" | "clock-out");
        lastScanTimeRef.current[String(userId)] = Date.now();

        const handleSuccessUI = (isOffline: boolean) => {
          setShowProcessingLoader(false);
          const msg = isOffline ? `Clocked ${isClockedIn ? 'out' : 'in'} successfully (Offline)` : `Successfully clocked ${isClockedIn ? 'out' : 'in'}!`;
          setScanStatus(msg);
          // dispatchToast(dispatch, "success", msg);
          const fallbackPhoto = rotatedImageBase64 ? (rotatedImageBase64.startsWith('data:') ? rotatedImageBase64 : `data:image/jpeg;base64,${rotatedImageBase64}`) : '';

          let employeeDetailsPhoto = null;
          if (bestMatch.employee_details) {
            try {
              const details = typeof bestMatch.employee_details === 'string' ? JSON.parse(bestMatch.employee_details) : bestMatch.employee_details;
              employeeDetailsPhoto = details?.biometric_authentication?.face?.photo;
              // console.log("FaceClockInOut: Parsed employeeDetailsPhoto", !!employeeDetailsPhoto);
            } catch (e) {
              // console.log("FaceClockInOut: Error parsing employee_details", e);
            }
          }

          let bioPhoto = null;
          if (bestMatch.biometric_authentication) {
            try {
              const bio = typeof bestMatch.biometric_authentication === 'string' ? JSON.parse(bestMatch.biometric_authentication) : bestMatch.biometric_authentication;
              bioPhoto = bio?.face?.photo;
              // console.log("FaceClockInOut: Parsed bioPhoto", !!bioPhoto);
            } catch (e) {
              // console.log("FaceClockInOut: Error parsing biometric_authentication", e);
            }
          }

          const photoToUseRaw = bestMatch.profile_pic || bioPhoto || employeeDetailsPhoto || bestMatch.face_photo || fallbackPhoto;
          let photoToUse = typeof photoToUseRaw === 'string' ? photoToUseRaw : fallbackPhoto;
          if (typeof photoToUse === 'string' && photoToUse.includes('data:image')) {
            // Robustly strip any duplicated data:image prefixes
            const parts = photoToUse.split(',');
            if (parts.length > 2 && parts[0].startsWith('data:image') && parts[1].startsWith('data:image')) {
              photoToUse = parts[parts.length - 2] + ',' + parts[parts.length - 1];
            } else {
              photoToUse = photoToUse.replace(/^(data:image\/[^;]+;base64,)+/, 'data:image/jpeg;base64,');
            }
          }

          // console.log("FaceClockInOut debug variables:", {
          //   "profile_pic": bestMatch.profile_pic,
          //   "bioPhoto": !!bioPhoto,
          //   "employeeDetailsPhoto": !!employeeDetailsPhoto,
          //   "face_photo": bestMatch.face_photo,
          //   "hasFallbackPhoto": !!fallbackPhoto,
          //   "photoToUsePreview": typeof photoToUse === 'string' ? (photoToUse.length > 50 ? photoToUse.substring(0, 50) + "..." : photoToUse) : typeof photoToUse,
          //   "isPersonalFaceScan": isPersonalFaceScan,
          // });

          const rawDept = bestMatch.department_name || bestMatch.department || 'N/A';
          const deptStr = typeof rawDept === 'string' ? rawDept : Array.isArray(rawDept) ? rawDept.map(d => d.name || d).join(", ") : 'N/A';

          setMatchedEmployeeData({
            name: decryptedName,
            department: deptStr,
            photo: photoToUse,
            fallbackPhoto: fallbackPhoto,
            isClockedIn
          });
          setCaptureCountdown(0);
          stopCountdownDisplay();
          successResetTimerRef.current = setTimeout(() => {
            successResetTimerRef.current = null;
            setMatchedEmployeeData(null);
            if (requiresScanButton) {
              handleStopScan("Press 'CLICK TO SCAN' to start");
            } else {
              setScanStatus("Align face in the frame...");
            }
            unlockProcessing();
            if (isPersonalFaceScan && onSuccessClose) {
              onSuccessClose();
            }
          }, 3000);
        };

        const doApiCall = async (locationData?: { off_site_loc_text?: string; country?: string }) => {
          const networkStatus = await Network.getStatus();
          const recordData = {
            user: userId,
            first_name,
            last_name,
            department: bestMatch.department_name,
            profile_pic: bestMatch.face_photo,
            type: attendanceType as "clock-in" | "clock-out",
            timestamp: TrueTimeService.getTrueTime().toISOString(),
            photoBase64: rotatedImageBase64 || "",
            photoKey,
            signature: Array.from(faceApiSignature) as number[],
            ppe_compliant: finalPayload.ppe_compliant,
            is_tampered: TrueTimeService.isTimeTampered(),
            off_site_loc_text: locationData?.off_site_loc_text || "",
            country: locationData?.country || undefined,
          };

          if (networkStatus.connected) {
            try {
              await new Promise<void>((resolve, reject) => {
                const { timestamp, ...apiPayload } = recordData;
                (dispatch as any)(
                  AddEmployeeAttendanceAPI(
                    {
                      ...apiPayload,
                      biometric_authentication_by: "face",
                      biometric_authentication: {
                        face: { photo: "", Signature: apiPayload.signature || [] },
                      },
                    },
                    () => resolve(),
                    (err: any) => reject(err)
                  )
                );
              });
              // Clock in/out succeeded -> server is definitely online and working!
              serverHealthService.markOnline();
              // console.log("Online: Clock in/out API called immediately");
            } catch (err: any) {
              const status = err?.response?.status;
              const isServerOrNetworkError =
                err?.message?.includes("No internet connection") ||
                err?.message?.includes("Network Error") ||
                err?.message?.includes("timeout") ||
                err?.code === "ECONNABORTED" ||
                err?.code === "ERR_NETWORK" ||
                (err?.isAxiosError && !err?.response) ||
                (status && status >= 500);

              if (isServerOrNetworkError) {
                console.log("Server or network error, falling back to enqueue and starting reconnection check:", err);
                serverHealthService.markOffline();
                await enqueueRecord(recordData);
                serverHealthService.startServerReconnectionPolling();
              } else {
                console.log("Backend validation failed. Not enqueuing.", err);

                // Try to extract the true error message from the backend response
                let errorMsg = err.message || "Attendance failed.";
                if (err?.response?.data) {
                  try {
                    const parsed = typeof err.response.data === "string" ? JSON.parse(err.response.data) : err.response.data;
                    if (parsed && parsed.message) {
                      errorMsg = parsed.message;
                    }
                  } catch (e) { }
                } else if (err.message) {
                  errorMsg = err.message;
                }

                throw new Error(errorMsg);
              }
            }
            handleSuccessUI(false);
          } else {
            await enqueueRecord(recordData);
            handleSuccessUI(true);
          }
        };

        if (isPersonalFaceScan && !isAttendanceTabletRole) {
          pendingOffSiteClockInFnRef.current = doApiCall;
          setShowOffSiteLocationModal(true);
        } else {
          await doApiCall();
        }
      };

      if (attendanceType === "clock-in" && bestMatch.isPPEKitRequired) {
        setPpePayload(attendancePayload);
        ppeClockInFnRef.current = executeClockIn;
        setPpeMatchUser({ decryptedName, ...bestMatch });
        // console.log("FaceClockInClockOut: showing PPE modal");

        const rawDept = bestMatch.department_name || bestMatch.department || 'N/A';
        const deptStr = typeof rawDept === 'string' ? rawDept : Array.isArray(rawDept) ? rawDept.map((d: any) => d.name || d).join(", ") : 'N/A';

        setMatchedEmployeeData({
          name: decryptedName,
          department: deptStr,
          photo: bestMatch.profile_pic || bestMatch.face_photo || (rotatedImageBase64 ? (rotatedImageBase64.startsWith('data:') ? rotatedImageBase64 : `data:image/jpeg;base64,${rotatedImageBase64}`) : ""),
          fallbackPhoto: "",
          isClockedIn: false
        });

        setShowPPEModal(true);
        return; // Halt execution and wait for modal confirmation
      }

      await executeClockIn();

    } catch (e: any) {
      console.error("Face clock error:", e);
      const errMsg = e.message || "Identification failed.";
      setShowProcessingLoader(false);
      setScanStatus(errMsg);
      setCaptureCountdown(0);
      stopCountdownDisplay();
      setScanErrorState(errMsg);
      document.body.classList.add("camera-error-toaster-active");
      // Restart the alignment countdown so the next attempt gets a fresh
      // "align face" cycle instead of instantly re-capturing after unlock.
      straightFaceStartTimeRef.current = null;

      const errorTimeoutMs = errMsg.toLowerCase().includes("approved payroll period") ? 5000 : 2000;

      errorResetTimerRef.current = setTimeout(() => {
        errorResetTimerRef.current = null;
        setScanErrorState(null);
        document.body.classList.remove("camera-error-toaster-active");
        if (requiresScanButton) {
          handleStopScan("Press 'CLICK TO SCAN' to start");
        } else {
          setScanStatus("Align face in the frame...");
        }
        unlockProcessing();
      }, errorTimeoutMs);
    }
  };

  const stopScanner = useCallback(async () => {
    // console.log("FaceClockInOut: stopScanner");
    // Remove web platform class when scanning stops
    if (!isNative) {
      document.documentElement.classList.remove("web-platform");
      document.body.classList.remove("web-platform");
      // Restore page styles after scanning stops
      document.body.style.removeProperty("background");
      document.body.style.removeProperty("background-color");
      document.documentElement.style.removeProperty("background");
      document.documentElement.style.removeProperty("background-color");
      document.body.style.removeProperty("overflow");
      document.documentElement.style.removeProperty("overflow");
      const rootEl = document.getElementById("root");
      if (rootEl) {
        rootEl.style.display = "";
      }
    }
    // Bump the session so any in-flight stale async stop work won't clobber
    // a fresh startScanner that may have been called in the meantime.
    const session = ++scanSessionRef.current;
    isScanningRef.current = false;
    setFaceDetected(false);
    facePresentRef.current = false;
    stopCountdownDisplay();
    clearFaceLostCheck();

    // CRITICAL: Fully release all idle/proximity state so dim mode and
    // sensors do NOT leak into other screens when this component unmounts.
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (isProximityMonitoringRef.current) {
      try {
        await Proximity.stopMonitoring();
      } catch (e) { }
      isProximityMonitoringRef.current = false;
    }
    if (isIdleModeRef.current) {
      try {
        await FaceDetection.setIdleMode({ enabled: false });
        await FaceDetection.setMotionDetection({ enabled: false });
      } catch (e) { }
      isIdleModeRef.current = false;
    }
    if (faceHideTimeoutRef.current) {
      clearTimeout(faceHideTimeoutRef.current);
      faceHideTimeoutRef.current = null;
    }
    try {
      await FaceDetection.stopScan();
      await FaceDetection.removeAllListeners();
    } catch {
      // Ignore errors during stop
    }

    // If a new session started while we were awaiting the native stop, abort
    // the rest of this stale cleanup so it doesn't tear down the new session.
    if (session !== scanSessionRef.current) {
      // console.log("FaceClockInClockOut: stopScanner stale, skipping brightness reset");
      return;
    }

    // Reset brightness back to user preference when scanning stops.
    // Native side only restores if `keepAwake` had previously captured it.
    if (Capacitor.isNativePlatform()) {
      try {
        await Proximity.allowSleep();
      } catch (e) {
        console.error("FaceClockInClockOut: allowSleep failed:", e);
      }
    }

    clearScanTimeout();
    clearSuccessResetTimer();
    clearErrorResetTimer();
    if (requiresScanButton) {
      setIsScanActive(false);
      isScanActiveRef.current = false;
    }
  }, [clearScanTimeout, clearSuccessResetTimer, clearErrorResetTimer, requiresScanButton]);

  useEffect(() => {

    const body = document.body;
    const html = document.documentElement;

    if (isNative) {
      bodyBgRef.current =
        body.style.background || body.style.backgroundColor || "";
      htmlBgRef.current =
        html.style.background || html.style.backgroundColor || "";

      body.style.setProperty("background", "transparent", "important");
      body.style.setProperty("background-color", "transparent", "important");
      html.style.setProperty("background", "transparent", "important");
      html.style.setProperty("background-color", "transparent", "important");

      try {
        if (Capacitor.getPlatform() === "android") {
          EdgeToEdge.setStatusBarColor({ color: "#0b0e14" });
          EdgeToEdge.setNavigationBarColor({ color: "#0b0e14" });
          // Dark background → use light (white) status bar icons/text
          StatusBar.setStyle({ style: Style.Dark });
        }
      } catch (e) { }

      body.style.overflow = "hidden";

      const rootEl = document.getElementById("root");
      if (rootEl) {
        rootDisplayRef.current = rootEl.style.display || "";
        rootEl.style.display = "none";
      }
    }

    if (Capacitor.isNativePlatform()) {
      Proximity.keepAwake().catch(() => { });
    }

    return () => {
      if (Capacitor.isNativePlatform()) {
        Proximity.allowSleep().catch(() => { });
      }
      if (isNative) {
        body.style.background = "";
        body.style.backgroundColor = "";
        html.style.background = "";
        html.style.backgroundColor = "";
        body.style.overflow = "";

        const rootEl = document.getElementById("root");
        if (rootEl) {
          rootEl.style.display = "";
        }

        // Reset Android status/nav bar to white (default) when scanner closes
        try {
          if (Capacitor.getPlatform() === "android") {
            EdgeToEdge.setStatusBarColor({ color: "#ffffff" });
            EdgeToEdge.setNavigationBarColor({ color: "#ffffff" });
            // White background → use dark status bar icons/text so they stay visible
            StatusBar.setStyle({ style: Style.Light });
          }
        } catch (e) { }
      }
    };
  }, [isNative]);





  useEffect(() => {
    if (!isNative && !IS_WEB_ATTENDANCE_ENABLED) return;
    if (showProcessingLoader) return;
    startScanner();
    return () => {
      stopScanner();
    };
  }, [startScanner, stopScanner, isNative, showProcessingLoader]);

  useEffect(() => {
    if (!isNative) return;

    let listenerHandle: any = null;

    const setupProximityListener = async () => {
      try {
        listenerHandle = await Proximity.addListener(
          "personNearby",
          async () => {
            // Only act on wake events while scanning is active
            if (!isScanningRef.current) return;
            await exitIdleMode();
          },
        );
      } catch (err) {
        console.error("Failed to setup proximity listener:", err);
      }
    };

    setupProximityListener();

    return () => {
      if (listenerHandle && typeof listenerHandle.remove === "function") {
        listenerHandle.remove();
      }
      if (isProximityMonitoringRef.current) {
        Proximity.stopMonitoring();
        isProximityMonitoringRef.current = false;
      }
    };
  }, [isNative, exitIdleMode]);



  const isScanningView = true;
  // Big web devices (desktop/laptop with mouse hover + fine pointer) NEVER
  // show the oval frame, glowing ring, or laser animation on web — even
  // when camera permission is granted. Small web devices (mobile/tablet/iPad)
  // and Capacitor apps keep the oval + animation like before.
  const isBigWebDevice =
    !isNative &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;


  const scannerContent = (
    <>


      <div
        className={`position-fixed scanner-overlay d-flex flex-column${isWebPermissionDenied ? " web-permission-denied" : ""}${isBigWebDevice ? " web-big-device" : ""}`}
      >
        {/* HEADER */}
        <div className="scanner-header d-flex justify-content-between align-items-start w-100">
          <div className="top-left-text">
            <h3 className="mb-2 fw-bold title-text">
              Face Scanner (Clock In/Out)
            </h3>
            <p className="mb-0 status-text">
              {scanStatus === "Align face in the frame..." ? "look straight at the camera" : scanStatus}
            </p>
          </div>

          {isScanningView && isPersonalFaceScan && (
            <div className="ms-3 top-right-dropdown">
              <CloseCircleFilled
                style={{ color: "white", fontSize: "28px", cursor: "pointer" }}
                onClick={() => {
                  if (onSuccessClose) {
                    onSuccessClose();
                  }
                }}
              />
            </div>
          )}

          {isScanningView && !isPersonalFaceScan && (
            <div className="top-right-dropdown">
              <Dropdown
                overlay={
                  <Menu style={{ minWidth: 180 }}>
                    {(() => {
                      const userData = getDecryptedUserData();
                      const roleName = String(userData?.role?.key || userData?.role?.name || "").toLowerCase();
                      if (roleName.includes("attendance tablet") || roleName.includes("attendance-tablet")) {
                        return (
                          <Menu.Item key="debug" onClick={() => navigate("/local-debug")}>
                            Local Debug
                          </Menu.Item>
                        );
                      }
                      return null;
                    })()}
                    <Menu.Item key="sync" onClick={async () => {
                      setIsManualSync(true);
                      try {
                        await triggerAttendanceSync();
                      } finally {
                        triggerSync();
                      }
                    }} disabled={isSyncing || isManualSync}>
                      {isManualSync ? (isSyncing ? `Syncing Users (${Math.round(syncProgress)}%)` : "Syncing Attendance...") : "Sync Data"}
                    </Menu.Item>
                    <Menu.Item key="update-app" onClick={() => navigate("/download-app")}>
                      Update App
                    </Menu.Item>
                    <Menu.Item key="logout" onClick={() => setShowLogoutModal(true)}>
                      Logout
                    </Menu.Item>
                    {/* Attendance Tablet (Check In/Out): restart the app without
                        touching the device — useful when the schedule has to be
                        re-armed or a stale state has to be cleared. */}
                    {isNative && Capacitor.getPlatform() === "android" && isAppLifecycleAvailable() && (
                      <Menu.Item
                        key="restart-app"
                        onClick={async () => {
                          const hideLoading = message.loading({
                            content: "Restarting app...",
                            key: "appRestart",
                            duration: 0,
                          });
                          try {
                            // Report the close and give the request a moment to complete
                            // before the process is killed (device tracking).
                            await trackDeviceCloseAndWait();
                            await AppLifecycle.restartApp({ delayMs: 1500, wakeScreen: true });
                          } catch (error) {
                            console.error("FaceClockInOut: restartApp failed:", error);
                            if (typeof hideLoading === "function") hideLoading();
                            message.error({ content: "Unable to restart the app", key: "appRestart" });
                          }
                        }}
                      >
                        Restart App
                      </Menu.Item>
                    )}
                    {/* Attendance Tablet (Check In/Out): the Android hardware
                        back button can't close the app — it must be closed
                        explicitly from here. */}
                    {isNative && Capacitor.getPlatform() === "android" && (
                      <Menu.Item
                        key="exit-app"
                        danger
                        onClick={async () => {
                          // Report the close and give the request a moment to complete
                          // before the process is killed (device tracking).
                          await trackDeviceCloseAndWait();
                          CapacitorApp.exitApp();
                        }}
                      >
                        Exit App
                      </Menu.Item>
                    )}
                  </Menu>
                }
                trigger={['click']}
                placement="bottomRight"
                getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
              >
                <button className="dropdown-btn shadow-sm d-flex align-items-center justify-content-center">
                  {(isSyncing && isManualSync) ? (
                    <Spin indicator={<LoadingOutlined spin />} />
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="1"></circle>
                      <circle cx="12" cy="5" r="1"></circle>
                      <circle cx="12" cy="19" r="1"></circle>
                    </svg>
                  )}
                </button>
              </Dropdown>
            </div>
          )}
        </div>

        <div className="scanner-container flex-grow-1">

          {/* CAMERA FRAME — full-screen video container on big web; oval on
               small web/native. NOT rendered when web permission is denied. */}
          {!isWebPermissionDenied && (
            <div className={`camera-frame ${isScanActive ? 'scanning' : 'idle'}`}>
              {/* Oval backdrop + ring/laser ONLY for small web & native.
                  NEVER for big web devices. */}
              {!isBigWebDevice && (
                <>
                  {/* The infinite dark overlay with a transparent hole */}
                  <div className="camera-hole-mask" />

                  {/* Glowing Ring & Laser */}
                  {isScanningView && (
                    <div className={`glowing-ring ${isScanActive && captureCountdown > 0 ? 'active' : ''} ${!isScanActive ? 'inactive' : ''}`}>
                      {isScanActive && (
                        <div className={`position-absolute laser-line ${captureCountdown > 0 ? 'active' : ''}`} />
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Countdown Text */}
              {isScanningView && captureCountdown > 0 && (
                <div
                  className="fw-bold text-center w-100 countdown-text"
                >
                  Hold still ... {captureCountdown}s
                </div>
              )}
            </div>
          )}

          {/* RIGHT / BOTTOM INFO CARDS */}
          {/* We keep this area always present so layout doesn't shift */}
          <div className="scanner-info-area">
            {scanErrorState && (
              <div className="bg-white shadow-lg slide-in error-card">
                <div className="error-icon-container">
                  <InfoCircleOutlined style={{ color: '#e03e46' }} />
                </div>
                <div className="error-message">
                  {scanErrorState}
                </div>
                <button
                  onClick={() => {
                    if (requiresScanButton) {
                      // Attendance Tablet mode — restart the scan immediately so
                      // the user does not have to press "Click to scan" again.
                      handleStartScan();
                    } else {
                      setScanErrorState(null);
                      setScanStatus("Align face in the frame...");
                      unlockProcessing();
                    }
                  }}
                  className="btn btn-dark w-100 py-2 try-again-btn"
                >
                  Try Again
                </button>
              </div>
            )}

            {showPPEModal && ppeMatchUser && (
              <div className="bg-white shadow-lg slide-in employee-card p-4 ppe-modal-card">
                <div className="d-flex align-items-start align-items-sm-center gap-2 gap-sm-3 mb-2 mb-sm-3">
                  <div className="flex-shrink-0">
                    {hr.ppe_compliant.wear}
                  </div>
                  <div className="d-flex flex-column">
                    <span className="fs-4 text-dark">Hi <strong className="fw-bold">{ppeMatchUser.decryptedName}</strong>,</span>
                    <span className="fs-4 text-dark mt-1">You are attempting to <strong className="text-success fw-bold">clock in</strong>.</span>
                  </div>
                </div>
                <h1 className="text-dark mb-2 mb-sm-3 pb-2 ppe-modal-title">
                  Are you currently wearing your required PPE kit?
                </h1>
                <div className="d-flex gap-3 w-100 mt-2">
                  <button
                    className="btn btn-outline-secondary w-50 fw-semibold ppe-modal-btn"
                    onClick={() => {
                      // console.log("FaceClockInClockOut: PPE modal confirmed as No (Not compliant)");
                      if (ppeClockInFnRef.current) {
                        ppeClockInFnRef.current({ ppe_compliant: false });
                        ppeClockInFnRef.current = null;
                      }
                      setShowPPEModal(false);
                      setPpePayload(null);
                      setPpeMatchUser(null);
                    }}
                  >
                    No
                  </button>
                  <button
                    className="btn btn-success w-50 fw-semibold ppe-modal-btn"
                    onClick={() => {
                      // console.log("FaceClockInClockOut: PPE modal confirmed");
                      if (ppeClockInFnRef.current) {
                        ppeClockInFnRef.current({ ppe_compliant: true });
                        ppeClockInFnRef.current = null;
                      }
                      setShowPPEModal(false);
                      setPpePayload(null);
                      setPpeMatchUser(null);
                    }}
                  >
                    Yes
                  </button>
                </div>
              </div>
            )}

            {/* Existing Overlays (FaceFound, RegForm, FaceList) updated to flow in the flex layout */}
          </div>

          {/* Scan Button (Attendance Tablet Mode) — always visible */}
          {requiresScanButton && (() => {
            const isIdle = !isScanActive && !matchedEmployeeData && !scanErrorState && !showPPEModal;
            return (
              <button
                type="button"
                className={`scan-trigger-btn${!isIdle ? ' scanning' : ''}`}
                onClick={handleStartScan}
              // onClick={isIdle ? handleStartScan : undefined}
              // disabled={!isIdle}
              >
                Click to scan
              </button>
            );
          })()}
        </div>


      </div>

      {/* NEW FULL SCREEN SUCCESS UI */}
      {matchedEmployeeData && !(showPPEModal && ppeMatchUser) && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column match-card">
          <div className={`flex-grow-1 d-flex flex-column align-items-center justify-content-center p-3 position-relative match-card-content ${matchedEmployeeData.isClockedIn ? 'bg-clock-out' : 'bg-clock-in'}`}>

            {/* CLOSE BUTTON */}
            <button
              className="position-absolute top-0 end-0 m-3 p-2 btn btn-link text-white text-decoration-none shadow-none"
              style={{ zIndex: 10 }}
              onClick={() => {
                setMatchedEmployeeData(null);
                if (requiresScanButton) {
                  handleStartScan();
                }
              }}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <div className="employee-img-container">
              {matchedEmployeeData.photo ? (
                <img
                  src={matchedEmployeeData.photo.startsWith('data:') ? matchedEmployeeData.photo : getFullAwsUrl(matchedEmployeeData.photo)}
                  alt="Employee"
                  onError={(e) => {
                    if (matchedEmployeeData.fallbackPhoto && e.currentTarget.src !== matchedEmployeeData.fallbackPhoto) {
                      e.currentTarget.src = matchedEmployeeData.fallbackPhoto;
                    } else {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement!.innerHTML = '<div class="w-100 h-100 d-flex align-items-center justify-content-center text-muted bg-light"></div>';
                    }
                  }}
                />
              ) : (
                <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted bg-light"></div>
              )}
            </div>
            <h1 className="text-white fw-bold mb-1 match-card-employee-name">{matchedEmployeeData.name}</h1>
            <h3 className="text-white fw-normal mb-4 pb-0 match-card-employee-department">{matchedEmployeeData.department}</h3>

            <div className={`bg-white shadow rounded-3 match-card-clock-in-out ${matchedEmployeeData.isClockedIn ? 'text-clock-out' : 'text-clock-in'}`}>
              {matchedEmployeeData.isClockedIn ? (
                <>Clocked Out<br />Successfully.</>
              ) : (
                <>Clocked In<br />Successfully.</>
              )}
            </div>
          </div>

          <div className="w-100 d-flex flex-column justify-content-center align-items-center px-4 position-relative bottom-action-container">
            {requiresScanButton && (
              <button
                className="btn btn-light w-100 fw-bold rounded-3 bottom-action-btn"
                onClick={() => {
                  setMatchedEmployeeData(null);
                  handleStartScan();
                }}
              >
                CLICK TO SCAN
              </button>
            )}
            {/* Home indicator mockup for aesthetics */}
            <div className="home-indicator-mockup"></div>
          </div>
        </div>
      )}
    </>
  );



  return (
    <>
      {createPortal(scannerContent, document.body)}
      {/* Full-screen "Please wait" loader while capturing/matching the face.
          Portaled last so it stacks above the scanner & all modals. */}
      {showProcessingLoader &&
        createPortal(
          <div className="face-scan-fullscreen-loader">
            <div className="face-scan-loader-box">
              <div className="face-scan-loader-spinner" />
              <p className="face-scan-loader-text mb-0">Getting Things Ready!</p>
              <span className="text-secondary fs-7">Please wait a moment while we load your information.</span>
            </div>
          </div>,
          document.body,
        )}
      {showPermissionDeniedModal &&
        createPortal(
          <>
            <PermissionDeniedModal
              isModalOpen={showPermissionDeniedModal}
              handleClose={() => setShowPermissionDeniedModal(false)}
              permissionName="Camera"
              onRetry={() => {
                setShowPermissionDeniedModal(false);
                setScanStatus("Requesting camera permission...");
                startScanner();
              }}
            />


          </>,
          document.body,
        )}


      {showLogoutModal && <Modal
        open={showLogoutModal}
        onCancel={() => setShowLogoutModal(false)}
        footer={null}
        centered
        className="logout-modal"
        rootClassName="logout-confirmation-modal"
      >
        <div className="text-center pb-4 pt-3">
          <h2 className="fs-4 fw-bolder mb-3 logout-title">Logout, Are You Sure?</h2>
          <p className="text-muted mb-4 fs-6 px-3 logout-description">
            Are you sure you want to log out of this attendance tablet? You will need to sign in again to resume clock-in and clock-out operations.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <button
              className="btn btn-outline-dark fs-6 fw-bold px-4 py-2 logout-modal-btn"
              onClick={() => setShowLogoutModal(false)}
            >
              CANCEL
            </button>
            <button
              disabled={authLoading}
              className="btn btn-danger fs-6 fw-bold px-4 py-2 logout-modal-btn"
              onClick={() => {
                setShowLogoutModal(false);
                logoutHandler();
              }}
            >
              {authLoading ? <BtnLoader /> : "LOGOUT"}
            </button>
          </div>
        </div>
      </Modal>}
      {showOffSiteLocationModal &&
        createPortal(
          <SelectOffSiteLocationModal
            open={showOffSiteLocationModal}
            loading={isOffSiteSubmitting}
            onCancel={() => {
              if (isOffSiteSubmitting) return;
              setShowOffSiteLocationModal(false);
              pendingOffSiteClockInFnRef.current = null;
              unlockProcessing();
              if (requiresScanButton) {
                handleStopScan("Press 'CLICK TO SCAN' to start");
              } else {
                setScanStatus("Align face in the frame...");
              }
            }}
            onSave={async (locationValues) => {
              console.log("Off site location selected:", locationValues);
              if (pendingOffSiteClockInFnRef.current) {
                try {
                  setIsOffSiteSubmitting(true);
                  await pendingOffSiteClockInFnRef.current(locationValues);
                  setShowOffSiteLocationModal(false);
                  pendingOffSiteClockInFnRef.current = null;
                } catch (err: any) {
                  console.error("Off site clock in failed:", err);
                  setShowOffSiteLocationModal(false);
                  pendingOffSiteClockInFnRef.current = null;
                  const errMsg = err?.message || "Attendance failed.";
                  setShowProcessingLoader(false);
                  setScanStatus(errMsg);
                  setCaptureCountdown(0);
                  unlockProcessing();
                  if (requiresScanButton) {
                    handleStopScan(errMsg);
                  }
                } finally {
                  setIsOffSiteSubmitting(false);
                }
              }
            }}
          />,
          document.body,
        )}
    </>
  );
};

export default FaceClockInOut;
